import { useState, useCallback } from 'react';
import { RefreshCw, Package, Plus } from 'lucide-react';
import type { StockResponse, RegionForecast } from '../../types/shared';
import { fetchStockLevels, fetchForecast } from '../../api/inventory';
import StockTable from './StockTable';
import ForecastSection from './ForecastSection';
import WarehouseTransferModal from './WarehouseTransferModal';
import WarehouseTransfersTable from './WarehouseTransfersTable';
import ClosePeriodSelector, { periodFromDate } from './ClosePeriodSelector';

type Tab = 'stock' | 'forecast' | 'transfers';

export default function InventoryPage() {
  const [activeTab, setActiveTab] = useState<Tab>('stock');
  const [stockData, setStockData] = useState<StockResponse | null>(null);
  const [forecast, setForecast] = useState<RegionForecast[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [transfersRefreshTrigger, setTransfersRefreshTrigger] = useState(0);
  const [period, setPeriod] = useState<string>(() => periodFromDate(new Date()));

  const loadStock = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchStockLevels();
      setStockData(data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stock levels.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchForecast();
      setForecast(data);
      setLastRefreshed(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load forecast.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data for the active tab on first view
  const handleTabChange = useCallback((tab: Tab) => {
    setActiveTab(tab);
    if (tab === 'stock' && stockData === null) loadStock();
    if (tab === 'forecast' && forecast === null) loadForecast();
    // Transfers tab loads data automatically in the component
  }, [stockData, forecast, loadStock, loadForecast]);

  // Initial load on mount
  useState(() => { loadStock(); });

  function handleRefresh() {
    if (activeTab === 'stock') loadStock();
    else loadForecast();
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Package size={22} className="text-moss-600 dark:text-moss-400" />
          <div>
            <h1 className="text-xl font-bold text-stone-900 dark:text-stone-100">Inventory</h1>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
              Trees by species &amp; pots — tracked per warehouse
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {activeTab === 'stock' && (
            <ClosePeriodSelector period={period} onChange={setPeriod} />
          )}
          {lastRefreshed && (
            <span className="text-xs text-stone-400 dark:text-stone-500">
              Updated {lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
          {activeTab === 'transfers' && (
            <button
              onClick={() => setIsTransferModalOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-moss-600 text-white hover:bg-moss-700 transition"
            >
              <Plus size={14} />
              New Transfer
            </button>
          )}
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-stone-300 dark:border-stone-600 text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-[#242d28] transition disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-stone-200 dark:border-stone-700 mb-6">
        {(['stock', 'forecast', 'transfers'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={`px-4 py-2 text-sm font-medium capitalize border-b-2 transition -mb-px ${
              activeTab === tab
                ? 'border-moss-500 text-moss-700 dark:text-moss-400'
                : 'border-transparent text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-300'
            }`}
          >
            {tab === 'stock' ? 'Stock' : tab === 'forecast' ? 'Forecast' : 'Transfers'}
          </button>
        ))}
      </div>

      {/* Error state */}
      {error && (
        <div className="mb-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Loading skeleton */}
      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="h-14 rounded-lg bg-stone-100 dark:bg-stone-800 animate-pulse"
            />
          ))}
        </div>
      )}

      {/* Stock tab */}
      {!loading && activeTab === 'stock' && stockData !== null && (
        <StockTable stockData={stockData} period={period} />
      )}

      {/* Forecast tab */}
      {!loading && activeTab === 'forecast' && forecast !== null && (
        <ForecastSection forecast={forecast} />
      )}

      {/* Transfers tab */}
      {activeTab === 'transfers' && (
        <WarehouseTransfersTable refreshTrigger={transfersRefreshTrigger} />
      )}

      {/* Transfer Modal */}
      <WarehouseTransferModal
        isOpen={isTransferModalOpen}
        onClose={() => setIsTransferModalOpen(false)}
        onSuccess={() => {
          setIsTransferModalOpen(false);
          setTransfersRefreshTrigger(prev => prev + 1);
        }}
      />
    </div>
  );
}
